"use client";

import { useState } from "react";
import Image from "next/image";
import { Globe, Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface ViewportResult {
  url: string;
  fetchedAt: string;
  viewport: string | null;
  language: string | null;
  charset: string | null;
  warnings: string[];
}


const normalizeInputUrl = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const ensured = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const url = new URL(ensured);
    if (url.protocol === "http:") url.protocol = "https:";
    url.hash = "";
    return url.toString();
  } catch {
    return null;
  }
};

const faviconFromUrl = (value: string): string | null => {
  const normalized = normalizeInputUrl(value);
  if (!normalized) return null;
  try {
    const url = new URL(normalized);
    return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=64`;
  } catch {
    return null;
  }
};

export function ViewportTool() {
  const [url, setUrl] = useState("");
  const [favicon, setFavicon] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ViewportResult | null>(null);

  const updateFavicon = (value: string) => {
    const next = faviconFromUrl(value);
    setFavicon(next);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!url.trim()) {
      setError("Please enter a valid URL.");
      return;
    }

    const normalized = normalizeInputUrl(url);
    if (!normalized) {
      setError("Please enter a valid URL.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/tools/viewport", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: normalized }),
      });
      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.error ?? "Unable to fetch viewport meta.");
      }

      setResult(json.data as ViewportResult);
    } catch (err) {
      setResult(null);
      setError(
        err instanceof Error ? err.message : "Something went wrong. Try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-2xl flex-col gap-3 sm:mx-auto sm:flex-row sm:items-center sm:gap-4"
      >
        <div className="relative flex-1">
          <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2">
            {favicon ? (
              <Image
                src={favicon}
                alt=""
                width={20}
                height={20}
                unoptimized
                className="h-5 w-5 sm:h-6 sm:w-6 rounded-sm"
              />
            ) : (
              <Globe className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
            )}
          </div>
          <span className="pointer-events-none absolute left-12 sm:left-14 top-1/2 h-6 sm:h-8 -translate-y-1/2 border-l border-border" />
          <Input
            type="text"
            placeholder="website.com"
            className="h-10 w-full pl-14 text-sm sm:h-12 sm:pl-16 sm:text-base border-2 border-border/20 bg-transparent focus:border-primary/50"
            value={url}
            onChange={(event) => {
              setUrl(event.target.value);
              updateFavicon(event.target.value);
              setError(null);
            }}
            autoComplete="url"
            aria-label="Website URL"
          />
        </div>
        <Button
          type="submit"
          size="lg"
          disabled={isLoading}
          className="shrink-0 h-10 w-full sm:h-12 sm:w-auto"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading
            </>
          ) : (
            "Check"
          )}
        </Button>
      </form>
      {error && (
        <p className="text-center text-sm font-medium text-destructive">
          {error}
        </p>
      )}

      {result ? (
        <div className="space-y-6">
          <Card>
            <CardHeader className="gap-1">
              <CardTitle>Mobile meta snapshot</CardTitle>
              <CardDescription>
                {result.url} · fetched{" "}
                {new Date(result.fetchedAt).toLocaleString()}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <MetaPanel
                label="Viewport tag"
                value={result.viewport ?? "Not set"}
              />
              <MetaPanel
                label="HTML lang"
                value={result.language ?? "Not set"}
              />
              <MetaPanel
                label="Charset"
                value={result.charset ?? "Not set"}
              />
            </CardContent>
          </Card>

          {result.warnings.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Issues detected</CardTitle>
                <CardDescription>
                  Fix these to avoid mobile rendering or indexing problems.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-rose-500">
                  {result.warnings.map((warning) => (
                    <li
                      key={warning}
                      className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3"
                    >
                      {warning}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <div className="mt-12 rounded-2xl border border-dashed border-border/60 p-8 text-center text-sm text-muted-foreground">
          Run your first scan to confirm viewport, language, and charset tags.
        </div>
      )}
    </div>
  );
}

function MetaPanel({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-background/60 p-4">
      <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 text-sm text-foreground break-words">{value}</p>
    </div>
  );
}
